import { lazy, Suspense } from "react"

import type { Route } from "@/api/models"
import type { IconName } from "lucide-react/dynamic"

import {
  Bot,
  Boxes,
  Briefcase,
  Gavel,
  GraduationCap,
  Languages,
  LayoutDashboard,
  LayoutGrid,
  type LucideIcon,
  Presentation,
  ScanText,
  Telescope,
  Users,
  Video,
  Wand2,
  Workflow,
} from "@/components/icons"

/**
 * Static fallback map keyed by route id. Renders instantly (no dynamic import),
 * so the gallery never flashes empty for the modules we ship. Customers can
 * override any module's icon via the backend `icon` field (a kebab-case lucide
 * name, e.g. "message-circle"), which takes precedence — see ModuleIcon below.
 */
const ROUTE_ICON_BY_ID: Record<string, LucideIcon> = {
  open: Telescope, // AI Explorer
  advantum: Boxes,
  agents: Bot, // Hosted agents (HERMES)
  workflows: Workflow, // Flows
  "ai-parser": ScanText,
  studio_ai: Wand2,
  translate: Languages,
  "video-insight": Video,
  "ai-portfolio": Briefcase,
  "slide-sense": Presentation,
  "local-ai-ambassador": Users, // Local AI Lead
  "admin-dashboard": LayoutDashboard,
  dash: LayoutDashboard,
  tender: Gavel,
  training: GraduationCap,
}

const FallbackIcon = LayoutGrid

/**
 * Lazily-resolved lucide icon for a customer-provided kebab-case name. Only
 * mounted when a route actually sets `icon`, so the common path (static map)
 * pays no dynamic-import cost.
 */
const DynamicIcon = lazy(() =>
  import("lucide-react/dynamic").then((m) => ({ default: m.DynamicIcon }))
)

type ModuleIconProps = {
  /** Route to render an icon for. Omit for synthetic entries (e.g. Training). */
  route?: Pick<Route, "id" | "icon">
  /** Static fallback when there is no route (e.g. the Training entry). */
  fallbackId?: string
  className?: string
}

export function ModuleIcon({ route, fallbackId, className }: ModuleIconProps) {
  // Backend-configured icon wins: render any lucide icon by name.
  if (route?.icon) {
    const StaticFallback = ROUTE_ICON_BY_ID[route.id] ?? FallbackIcon
    // DynamicIcon shows `fallback` while the icon loads AND keeps showing it if
    // the name is invalid, so a bad customer icon degrades to the static icon
    // instead of an empty tile. The outer Suspense covers the dynamic-module load.
    const renderFallback = () => <StaticFallback className={className} />
    return (
      <Suspense fallback={renderFallback()}>
        <DynamicIcon
          className={className}
          fallback={renderFallback}
          name={route.icon as IconName}
        />
      </Suspense>
    )
  }

  const Icon = ROUTE_ICON_BY_ID[route?.id ?? fallbackId ?? ""] ?? FallbackIcon
  return <Icon className={className} />
}
